<script setup lang="ts">
import sortBy from 'lodash/sortBy'
import Fuse from 'fuse.js'
import { useGeoDataStore } from '~/stores/geoData'
import { County, Municipality, Page } from '@/types/types'
import AppHeading from '~/components/app/AppHeading.vue'
import LayoutSection from '~/components/layout/LayoutSection.vue'
import SearchInput from '~/components/form/SearchInput.vue'
import AppLoadingWrapper from '~/components/app/AppLoadingWrapper.vue'
import AppMap from '@/components/map/AppMap.vue'
import { LayerPresets } from '~/types/map'

useSeoMeta({
  title: () => `Drivmedla - Utforska en plats på kartan`,
  description: () =>
    'Med hjälp av vår karta kan du utforska elnätsägare, fordonsflotta, placering av fossilfria drivmedelsstationer och etableringsbehov i ett valt län eller kommun.',
  ogTitle: () => `Drivmedla - Utforska en plats på kartan`,
  ogDescription: () =>
    'Med hjälp av vår karta kan du utforska elnätsägare, fordonsflotta, placering av fossilfria drivmedelsstationer och etableringsbehov i ett valt län eller kommun.',
  ogLocale: () => 'sv_SE',
  ogSiteName: () => 'Drivmedla',
  ogUrl: () => `${useRuntimeConfig().public.baseURL}${useRoute().fullPath}`,
  ogImage: () => `${useRuntimeConfig().public.baseURL}/_nuxt/assets/_logo-horizontal-GREEN.svg`,
})
const activeTabIndex = ref<number>(0)
const layerPresetsCounties: LayerPresets = {
  counties: { visible: true },
}
const layerPresetsMunicipalities: LayerPresets = {
  municipalities: { visible: true },
}
const { findOne } = useStrapi()
const { data: pageInfo } = await useAsyncData(
  'explore-page',
  () => findOne<Page>('explore-page', { populate: '*' }),
  { transform: (_pageInfo) => _pageInfo.data }
)

const geoDataStore = useGeoDataStore()
const { pending: municipalitiesPending } = await useAsyncData('municipalities', () =>
  geoDataStore.initMunicipalities()
)
const municipalities = computed<Municipality[]>(() => geoDataStore.municipalities)

const { pending: countiesPending } = await useAsyncData('counties', () =>
  geoDataStore.initCounties()
)
geoDataStore.initMunicipalities()
geoDataStore.initCounties()
const counties = computed<County[]>(() => geoDataStore.counties)
const search = reactive<{ value: string }>({ value: '' })

const searchResultCounties = computed(() => {
  if (counties.value) {
    let filtered = toRaw(counties.value)

    if (search.value) {
      const fuse = new Fuse(filtered, { threshold: 0.3, keys: ['name'] })
      const result = fuse.search(search.value)
      const formattedResult = result.map((res) => res.item)
      filtered = [...formattedResult]
    }
    return filtered
  }
})
const searchResultMunicipalities = computed(() => {
  if (municipalities.value) {
    let filtered = toRaw(municipalities.value)

    if (search.value) {
      const fuse = new Fuse(filtered, { threshold: 0.2, keys: ['name'] })
      const result = fuse.search(search.value)
      const formattedResult = result.map((res) => res.item)
      filtered = [...formattedResult]
    }
    return filtered
  }
})
</script>

<template>
  <main>
    <AppHero
      :title="pageInfo?.attributes.generalContent.title"
      :description="pageInfo?.attributes.generalContent.ingress"
      background-color="#849FED"
      color="#1F2937"
      style="padding-bottom: 30px"
    />
    <AppLoadingWrapper :is-loading="municipalitiesPending || countiesPending" :height="200">
      <TabView v-model:activeIndex="activeTabIndex" class="tabs">
        <TabPanel v-if="counties" header="Län">
          <LayoutSection>
            <SearchInput v-model:value="search.value" placeholder="Sök på län" />
            <AppHeading title="Sveriges län (A-Ö)" type="h2" size="large" />
            <div class="regions-wrapper">
              <div class="regions">
                <div class="location">
                  <NuxtLink
                    v-for="county in sortBy(searchResultCounties, 'name')"
                    :key="county.id"
                    class="location__item"
                    :to="`/utforska-en-plats/region/${county.slug}`"
                  >
                    {{ county.name }}
                  </NuxtLink>
                </div>
              </div>
              <div class="map">
                <main class="map-wrapper">
                  <AppMap
                    v-if="activeTabIndex === 0"
                    :layer-presets="layerPresetsCounties"
                    :hide-tile-layer="false"
                  />
                </main>
              </div>
            </div>
          </LayoutSection>
        </TabPanel>
        <TabPanel v-if="municipalities" header="Kommun">
          <LayoutSection>
            <SearchInput v-model:value="search.value" placeholder="Sök på kommun" />
            <AppHeading title="Sveriges kommuner (A-Ö)" type="h2" size="large" />
            <div class="regions-wrapper">
              <div class="regions">
                <div class="location">
                  <NuxtLink
                    v-for="municipality in sortBy(searchResultMunicipalities, 'name')"
                    :key="municipality.municipality_code"
                    class="location__item"
                    :to="`/utforska-en-plats/kommun/${municipality.slug}`"
                  >
                    {{ municipality.name }}
                  </NuxtLink>
                </div>
              </div>
              <div class="map">
                <main class="map-wrapper">
                  <AppMap
                    v-if="activeTabIndex === 1"
                    :layer-presets="layerPresetsMunicipalities"
                    :hide-tile-layer="false"
                  />
                </main>
              </div>
            </div>
          </LayoutSection>
        </TabPanel>
      </TabView>
    </AppLoadingWrapper>
  </main>
</template>
<style lang="scss" scoped>
@import 'App.scss';
.regions-wrapper {
  display: flex;
  flex-flow: row wrap;
}
.regions,
.map {
  margin: 1rem 0;
  width: 100%;

  @include tablet-landscape-up {
    margin: 0;
    width: 50%;
  }
}
.location {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 1.5rem;

  &__item {
    display: flex;
    color: #111827;
    font-weight: 500;
    text-transform: none;
    font-size: 1rem;
    background-color: $lightGray;
    padding: 10px 15px 10px 20px;
    border-left: 6px $active solid;
    border-radius: 0 4px 4px 12px;

    @include tablet-landscape-up {
      font-size: 1.2rem;
    }

    &:hover {
      background-color: #fff;
    }
  }
  .icon {
    color: $dark;
    width: 2rem;
  }
}
.map-wrapper {
  height: 80vh;
  margin: 0;
  position: sticky;
  top: 90px;

  @include tablet-landscape-up {
    margin: 0 2rem;
  }
}
</style>
